/**
 * UserPicker — inline-выбор пользователя в стиле Linear/Notion.
 *
 * В свёрнутом виде показывает аватар + имя (или placeholder если ничего не выбрано).
 * По клику открывается единый Popover с:
 *  - search-input сверху;
 *  - inline-списком пользователей (без вложенного Combobox);
 *  - первой строкой "Не назначен" (для null);
 *  - чекмарком на текущем выбранном.
 *
 * Список грузится один раз при первом открытии через `userRepository.onlyHumanActive().orderByName()`.
 * Поиск — локальный по name/email.
 *
 * Если canEdit=false — display рендерится без интерактивности.
 *
 * Готовый блок для полей "Ответственный", "Менеджер", "Assignee" и т.п.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Group,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  type MantineSize,
} from '@mantine/core';
import { IconCheck, IconUserOff } from '@tabler/icons-react';
import { InlinePicker } from '@ayocore/platform/client/components/InlinePicker';
import { apiBaseUrl } from '@ayocore/platform/client/helpers/fetcher';
import { useUserRepository } from '../rpc';
import { useUsersController } from '../UsersController';
import type { User } from '../../schemas/user.schema';

export interface UserPickerProps {
  /** ID выбранного пользователя. */
  value: number | null;
  /** Колбек с новым ID (или null при очистке). */
  onChange: (userId: number | null) => void;
  /**
   * Объект пользователя для рендера display (имя + аватар). Прокидывается из родителя
   * (lead.manager, task.assignee и т.д.) — там уже доступен avatar_url через JOIN.
   */
  user?: {
    id: number;
    name: string;
    avatar_url?: string | null;
    email?: string | null;
  } | null;
  /** Плейсхолдер когда user пустой. По умолчанию "Не назначен". */
  placeholder?: string;
  /** Если false — display только, без popover. */
  canEdit?: boolean;
  /** Размер свёрнутого display (аватар + текст). По умолчанию 'xs'. */
  size?: MantineSize;
  /** Размер содержимого popover (поиск + строки опций). По умолчанию 'sm'. */
  inputSize?: MantineSize;
}

/** Преобразует относительный URL аватарки (из filemanager) в абсолютный — аналогично UserCell. */
function resolveAvatarSrc(url: string | null | undefined): string | null {
  if (!url) return null;
  return url.startsWith('http') ? url : `${apiBaseUrl}${url}`;
}

type UserOptionRowProps = {
  label: string;
  avatarName?: string;
  avatarUrl?: string | null;
  /** Если true — отрисуем серую "пустую" иконку вместо аватара (для строки "Не назначен"). */
  emptyIcon?: boolean;
  selected: boolean;
  onClick: () => void;
  size: MantineSize;
};

function UserOptionRow({
  label,
  avatarName,
  avatarUrl,
  emptyIcon,
  selected,
  onClick,
  size,
}: UserOptionRowProps) {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor: 'pointer',
        padding: '6px 8px',
        borderRadius: 4,
        background: hovered ? 'var(--mantine-color-gray-1)' : 'transparent',
        transition: 'background 0.1s ease',
      }}
    >
      <Group gap={8} wrap='nowrap' justify='space-between'>
        <Group gap={8} wrap='nowrap' style={{ minWidth: 0 }}>
          {emptyIcon ? (
            <Avatar size={size} radius='xl' color='gray'>
              <IconUserOff size={14} />
            </Avatar>
          ) : (
            <Avatar
              src={avatarUrl ?? undefined}
              size={size}
              radius='xl'
              name={avatarName}
              color='initials'
            />
          )}
          <Text
            size={size}
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
          >
            {label}
          </Text>
        </Group>
        {selected ? <IconCheck size={14} color='var(--mantine-color-blue-6)' /> : null}
      </Group>
    </Box>
  );
}

export function UserPicker({
  value,
  onChange,
  user,
  placeholder = 'Не назначен',
  canEdit = true,
  size = 'sm',
  inputSize = 'sm',
}: UserPickerProps) {
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const userRepository = useUserRepository();
  const { getUser } = useUsersController();

  // Lazy-загрузка списка при первом открытии popover.
  // Используем явный chain через where/orderBy — onlyHumanActive() возвращает this,
  // из-за чего TS теряет Promise-wrapping AwaitableRpcClient.
  useEffect(() => {
    if (!opened || users.length > 0) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const rows = await userRepository
          .where('is_active', true)
          .orderBy('name', 'asc')
          .limit(500)
          .fetch();
        // system-юзеры (CRON_SERVICE, OAUTH_SERVICE и т.д.) — отрезаем на клиенте,
        // т.к. whereNull не доступен в AwaitableRpcClient.
        const humanRows = (rows ?? []).filter(u => !u.system_code);
        if (!cancelled) setUsers(humanRows);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opened, users.length, userRepository]);

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.trim().toLowerCase();
    return users.filter(
      u =>
        (u.name && u.name.toLowerCase().includes(q)) ||
        (u.email && u.email.toLowerCase().includes(q))
    );
  }, [users, search]);

  const userName = user?.name ?? user?.email ?? null;
  const displayAvatarUrl =
    user?.avatar_url ?? (user ? resolveAvatarSrc(getUser(user.id)?.avatarThumbUrl) : null);

  const display = (
    <Group gap={6} wrap='nowrap' style={{ minWidth: 0 }}>
      <Avatar
        src={displayAvatarUrl ?? undefined}
        size={size}
        radius='xl'
        name={userName ?? undefined}
        color='initials'
      />
      <Text
        size={size}
        c={userName ? '#518e81' : 'dimmed'}
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {userName ?? placeholder}
      </Text>
    </Group>
  );

  const selectUser = (id: number | null) => {
    onChange(id);
    setOpened(false);
    setSearch('');
  };

  return (
    <InlinePicker
      display={display}
      opened={opened}
      onOpenChange={setOpened}
      canEdit={canEdit}
      width={300}
    >
      <Stack gap={6}>
        <TextInput
          value={search}
          onChange={event => setSearch(event.currentTarget.value)}
          placeholder='Поиск...'
          size={inputSize}
          autoFocus
        />
        <ScrollArea.Autosize mah={300} type='hover' scrollbarSize={6} scrollbars='y'>
          <Stack gap={2}>
            <UserOptionRow
              label='Не назначен'
              emptyIcon
              selected={value === null}
              onClick={() => selectUser(null)}
              size={inputSize}
            />
            {filtered.map(u => {
              const cached = getUser(u.id);
              return (
                <UserOptionRow
                  key={u.id}
                  label={u.name ?? u.email ?? `User #${u.id}`}
                  avatarName={u.name ?? u.email ?? undefined}
                  avatarUrl={resolveAvatarSrc(cached?.avatarThumbUrl)}
                  selected={u.id === value}
                  onClick={() => selectUser(u.id)}
                  size={inputSize}
                />
              );
            })}
            {loading ? (
              <Text size='xs' c='dimmed' ta='center' py='xs'>
                Загрузка...
              </Text>
            ) : null}
            {!loading && filtered.length === 0 && search ? (
              <Text size='xs' c='dimmed' ta='center' py='xs'>
                Никого не найдено
              </Text>
            ) : null}
          </Stack>
        </ScrollArea.Autosize>
      </Stack>
    </InlinePicker>
  );
}
